import {useEffect, useState} from "react";

// Gaps between capture attempts. Short at first, because a payment that goes through usually
// settles within the first seconds, then widening so an abandoned one stops costing Mollie API
// calls. Seven gaps means eight attempts spanning ~28 seconds.
const RETRY_DELAYS_MS = [1000, 2000, 3000, 4000, 5000, 6000, 7000];

// Mollie settles asynchronously and the redirect back is not a reliable status signal: the bank
// reports the outcome to Mollie on its own schedule, so the capture endpoint can still answer
// "pending" for a payment that does go through. The endpoint is idempotent, so keep asking for
// about half a minute before settling on the "pending" state, which is a truthful end state for
// both a slow bank and an abandoned payment.
export const usePaymentStatusPolling = (capture, token, onSuccess) => {
    const [paymentStatus, setPaymentStatus] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);

    useEffect(() => {
        if (!token) {
            setPaymentStatus("aborted");
            return;
        }

        const abortController = new AbortController();
        let retryTimeout = null;
        let cancelled = false;

        const attempt = (attemptIndex) => {
            capture(token, abortController.signal)
                .then((data) => {
                    if (cancelled) {
                        return;
                    }

                    if (data === "pending") {
                        const retryDelay = RETRY_DELAYS_MS[attemptIndex];

                        if (retryDelay !== undefined) {
                            retryTimeout = setTimeout(() => attempt(attemptIndex + 1), retryDelay);
                            return;
                        }

                        // Out of attempts: the bank has not reported back yet. This is a normal
                        // outcome for a transfer, the webhook settles the order later on.
                        setPaymentStatus("pending");
                        return;
                    }

                    if (data === "success") {
                        setPaymentStatus("success");
                        onSuccess();
                    }
                    else if (data === "event_is_full") {
                        setPaymentStatus("event_full");
                    }
                    else if (data === "order_no_longer_pending") {
                        setPaymentStatus("expired");
                    }
                    else {
                        setPaymentStatus("general_error");
                    }
                })
                .catch((error) => {
                    if (cancelled) {
                        return;
                    }

                    console.error(error);

                    let errorData;
                    try {
                        errorData = JSON.parse(error.message);
                    } catch (parseError) {
                        errorData = null;
                    }

                    if (errorData?.code === 913) {
                        setErrorMessage("Die Zahlung wurde von deiner Bank nicht abgeschlossen.");
                    }

                    setPaymentStatus("general_error");
                });
        };

        attempt(0);

        return () => {
            cancelled = true;
            clearTimeout(retryTimeout);
            abortController.abort();
        };
    }, [capture, token, onSuccess]);

    return {paymentStatus, errorMessage};
};
